import { IdConta, IdContato, IdInbox, IdUsuario } from "@/services/common-types";
import { ContatosService, CreateContato, FiltroContatoImportacaoDto, ImportarContatosDto, ResponseContato, SincronizarContatosDto, UpdateContato } from "@/services/contatos/contatos";
import { defineStore } from "pinia";
import { useAuthStoreUser } from "./authUser";
import { SincronizacaoContatosEventPayload, WebsocketService } from "../../services/websocket/websocket.service";
import { toast } from "vue3-toastify";
import { ResponseUsuario } from "../../services/usuarios/usuarios.service";

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

export const useContatoStore = defineStore('contato', {
  state: () => ({
    initialized: false as boolean,
    idConta: undefined as IdConta | undefined,
    contatos: [] as ResponseContato[],
    service: new ContatosService(),
    userAuthStore: useAuthStoreUser(),
    websocketService: null as null | WebsocketService,
    imageVersions: {} as Record<string, number>,
    mapaContatoExistenciaWpp: new Map<string, boolean>(),
    paginaServidor: 0 as number,
    totalContatosServidor: 0 as number,
    isLoadingMore: false as boolean,
    buscaTextual: undefined as string | undefined,
    idEtiqueta: undefined as string | undefined,
  }),
  actions: {
    async init(idConta: IdConta, force: boolean = false) {
      if (this.initialized && !force) return;

      if (!this.idConta && !idConta) {
        throw new Error(`[ContatoStore] Store inicializada sem ID de conta.`);
      }

      this.mapaContatoExistenciaWpp = new Map<string, boolean>();
      this.idConta = idConta;
      this.contatos = [];
      this.paginaServidor = 0;
      this.totalContatosServidor = 0;
      this.isLoadingMore = false;
      this.buscaTextual = undefined;
      this.idEtiqueta = undefined;

      this.initialized = true;
      await this.loadNextPage();
      return this;
    },

    async loadNextPage() {
      if (this.isLoadingMore) return;
      if (this.paginaServidor > 0 && this.contatos.length >= this.totalContatosServidor) return;

      this.isLoadingMore = true;
      try {
        const nextPage = this.paginaServidor + 1;
        const result = await this.service.getByContaPaginated(this.idConta!, nextPage, 100, this.buscaTextual, this.idEtiqueta);
        const newContacts = result.dados.map(c => this._applyCacheBuster(c));
        this.contatos.push(...newContacts);
        this.totalContatosServidor = result.total;
        this.paginaServidor = result.pagina;
      } finally {
        this.isLoadingMore = false;
      }
    },

    async resetAndSearch(buscaTextual?: string, idEtiqueta?: string) {
      this.contatos = [];
      this.paginaServidor = 0;
      this.totalContatosServidor = 0;
      this.buscaTextual = buscaTextual || undefined;
      this.idEtiqueta = idEtiqueta || undefined;
      await this.loadNextPage();
    },

    /**
     * Outro contato (≠ `idContato`) da conta que já tem esse número de WhatsApp.
     * Consultado ANTES do PATCH para o front mostrar quem é o dono e oferecer abrir
     * o contato, sem depender do texto do 400 do servidor. `/by-codigo` normaliza
     * igual ao update (9º dígito) e ignora excluídos. Falha da consulta → `null`:
     * o PATCH segue e o servidor continua barrando o duplicado.
     */
    async buscarOutroContatoComCodigo(idConta: string, idContato: string, codigo: string): Promise<ResponseContato | null> {
      try {
        const existente = await this.service.getByCodigo(idConta, codigo);
        return existente && existente.id !== idContato ? existente : null;
      } catch (e) {
        console.error('[contatoStore] [buscarOutroContatoComCodigo]', e);
        return null;
      }
    },

    async findByIdCached(idContato: IdContato): Promise<ResponseContato | null> {
      const cached = this.contatos.find(c => c.id === idContato);
      if (cached) return cached;
      try {
        const fromApi = await this.service.getById(idContato);
        if (fromApi) this._patchContato(fromApi);
        return fromApi;
      } catch {
        return null;
      }
    },

    async searchContatos(buscaTextual: string, resultados = 10): Promise<ResponseContato[]> {
      if (!this.idConta || !buscaTextual) return [];
      try {
        const result = await this.service.getByContaPaginated(this.idConta, 1, resultados, buscaTextual);
        return result.dados;
      } catch {
        return [];
      }
    },

    // async _initWebSocket() {
    //   this.websocketService = await this.userAuthStore.initWebsocket();

    //   this.websocketService.registerCallback('SINCRONIZACAO_CONTATOS.CONCLUIDA', (payload: SincronizacaoContatosEventPayload) => {
    //     if (payload.idConta != this.idConta) return;
    //     console.log('sincronizacao de contatos concluida', payload);
    //     toast.success('Sincronização de contatos concluída');
    //     // Recarrega a lista para pegar as fotos novas
    //     this.getAll();
    //   });

    //   this.websocketService.registerCallback('SINCRONIZACAO_CONTATOS.ERRO', (payload: SincronizacaoContatosEventPayload) => {
    //     if (payload.idConta != this.idConta) return;
    //     console.log('erro ao sincronizar contatos', payload);
    //     toast.error('Erro ao sincronizar contatos');
    //   });
    // },

    async getById(idContato: IdContato) {
      this.throwIfUninitialized();
      return await this.service.getById(idContato);
    },

    _applyCacheBuster(contact: ResponseContato): ResponseContato {
      // FIX: Se for Base64 (upload manual recente), não adiciona cache buster para não quebrar a string
      if (contact.imagem && contact.imagem.startsWith('data:')) {
        return contact;
      }

      if (contact.imagem && this.imageVersions[contact.id]) {
        const separator = contact.imagem.includes('?') ? '&' : '?';
        return {
          ...contact,
          imagem: `${contact.imagem}${separator}v=${this.imageVersions[contact.id]}`
        };
      }
      return contact;
    },

    async getAll() {
      this.throwIfUninitialized();
      const result = await this.service.getByConta(this.idConta!);
      this.contatos = result.map(c => this._applyCacheBuster(c));
      this.paginaServidor = 0;
      this.totalContatosServidor = result.length;
      return this.contatos;
    },

    _patchContato(updated: ResponseContato) {
      const idx = this.contatos.findIndex(c => c.id === updated.id);
      if (idx !== -1) this.contatos[idx] = this._applyCacheBuster(updated);
      else this.contatos.push(this._applyCacheBuster(updated));
    },

    //  AÇÃO DE CRIAÇÃO
    async createWithUpload(data: CreateContato & { file?: File | null }) {
      this.throwIfUninitialized();

      const { file, ...rest } = data;

      const payload: CreateContato = { ...rest, imagem: null } as CreateContato;
      const result = await this.service.create(payload);

      if (file) {
        await this.service.uploadFoto(result.id, file, rest.codigo);
        this.imageVersions[result.id] = Date.now();
      }

      this._patchContato(result);
      return result;
    },

    async update(data: UpdateContato) {
      await this.service.update(data);
      const updated = await this.service.getById(data.id);
      if (updated) this._patchContato(updated);
      return updated;
    },

    async updateWithUpload(data: UpdateContato & { file?: File | null }) {
      this.throwIfUninitialized();

      const { file, ...rest } = data;
      await this.service.update({ ...rest } as UpdateContato);

      if (file) {
        await this.service.uploadFoto(data.id, file, rest.codigo!);
        this.imageVersions[data.id] = Date.now();
      }

      const updated = await this.service.getById(data.id);
      if (updated) this._patchContato(updated);
      return updated;
    },

    async delete(idContato: IdContato) {
      this.throwIfUninitialized();
      const result = await this.service.delete(idContato);
      this.contatos = this.contatos.filter(c => c.id !== idContato);
      return result;
    },

    /** Devolve `{ url, motivo? }` CRU: quem chama decide o que dizer ao operador
     *  (ver `reagirAoResultadoDaFoto`). Com `motivo`, nada foi buscado. */
    async buscarFotoPerfil(idContato: IdContato, codigo: string, idConta: IdConta) {
      const result = await this.service.buscarFotoPerfil(codigo, idConta);
      if (result?.url) {
        this.imageVersions[idContato] = Date.now();
        const idx = this.contatos.findIndex(c => c.id === idContato);
        if (idx !== -1) {
          this.contatos[idx] = this._applyCacheBuster({
            ...this.contatos[idx],
            imagem: result.url
          });
        }
      }
      return result;
    },

    async sincronizar(data: SincronizarContatosDto) {
      return await this.service.sincronizar(data);
    },

    async importar(data: ImportarContatosDto) {
      return await this.service.importar(data);
    },

    async listarImportacoes(data: FiltroContatoImportacaoDto) {
      return await this.service.listarImportacoes(data);
    },

    async atribuirAtendenteFixo(idContato: IdContato, idAtendente: IdUsuario, idInbox: IdInbox): Promise<ResponseUsuario> {
      return await this.service.atribuirAtendenteFixo(idContato, idAtendente, idInbox);
    },

    async buscarAtendenteFixo(idContato: IdContato, idInbox: IdInbox): Promise<ResponseUsuario | null> {
      return await this.service.buscarAtendenteFixo(idContato, idInbox);
    },

    async removerAtendenteFixo(idContato: IdContato, idInbox: IdInbox) {
      return await this.service.removerAtendenteFixo(idContato, idInbox);
    },

    async limparContatos015(idConta: string) {
      return await this.service.limparContatos015(idConta);
    },

    async verificarExistenciaWpp(idContato: string) {
      if (this.mapaContatoExistenciaWpp.has(idContato)) return this.mapaContatoExistenciaWpp.get(idContato);

      const { existe } = await this.service.verificarExistenciaWpp(idContato);

      if (existe !== undefined) {
        this.mapaContatoExistenciaWpp.set(idContato, existe);
      }

      return existe;
    },

    throwIfUninitialized() {
      if (!this.initialized) throw new Error('[contatoStore] Método chamado com a Store não inicializada.');
    }
  }
});
